/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { useState } from "react";
import { IUserSubscriptionModel } from "@nf/common";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";

import {
  useCurrentSubscriptions,
  usePreviousSubscriptions,
  useUserSubscriptions,
} from "hooks";
import { CommonTextError } from "../../CommonTextError";
import { SubscriptionList } from "./components/SubscriptionList/SubscriptionList";

const NoSubscriptionsContainer = dynamic(
  () => import("components/Settings/NoSubscriptionContainer")
);

const CancellationDialog = dynamic(
  () => import("components/CancellationDialog/CancellationDialog"),
  { ssr: false }
);

const CancelPauseDialog = dynamic(
  () => import("components/CancelPauseDialog/CancelPauseDialog"),
  { ssr: false }
);

export const SettingsSubscription = () => {
  const { t } = useTranslation("translations");
  const { subscriptions, error } = useUserSubscriptions();
  const { currentSubscriptions, hasCurrentSubscriptions } =
    useCurrentSubscriptions(subscriptions);
  const { previousSubscriptions, hasPreviousSubscriptions } =
    usePreviousSubscriptions(subscriptions);

  const [subscriptionToCancel, setSubscriptionToCancel] =
    useState<IUserSubscriptionModel>();
  const [isCancelPauseDialogVisible, setIsCancelPauseDialogVisible] =
    useState(false);

  if (error) {
    return <CommonTextError translationKey="web_my_account_error" />;
  }

  if (!subscriptions?.length) {
    return <NoSubscriptionsContainer />;
  }

  return (
    <>
      {!!currentSubscriptions?.length && (
        <SubscriptionList
          title={t("web_subscription_current")}
          subscriptions={currentSubscriptions as IUserSubscriptionModel[]}
          hasActiveSub={true}
          onCancel={setSubscriptionToCancel}
          onCancelPause={() => setIsCancelPauseDialogVisible(true)}
        />
      )}

      {hasPreviousSubscriptions && (
        <SubscriptionList
          title={t("web_subscription_previous")}
          subscriptions={previousSubscriptions as IUserSubscriptionModel[]}
          hasActiveSub={hasCurrentSubscriptions}
        />
      )}

      {subscriptionToCancel && (
        <CancellationDialog
          subscription={subscriptionToCancel}
          visible
          onCancel={() => setSubscriptionToCancel(undefined)}
        />
      )}

      <CancelPauseDialog
        visible={isCancelPauseDialogVisible}
        onCancel={() => setIsCancelPauseDialogVisible(false)}
      />
    </>
  );
};

export default SettingsSubscription;
